import { UserAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { useManagePermittedRoles } from 'library/hooks';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeopleByPersonID } from 'reduxStore/people/selectors';
import { peopleActions } from 'reduxStore/people/slice';
import { AutoComplete, Button, Col, Flex, Form, Input, Modal, Row, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ChangePersonType = ({ visible: visibleProp, setVisible: setVisibleProp, children, personID }) => {
	// ✅ Hooks
	const dispatch = useDispatch();
	const canChangeUserType = useManagePermittedRoles('changeUserType');
	const permissionDeniedMessage = "You don't have permission to perform this action.";
	const [form] = Form.useForm();
	const {
		token: {
			fontSizeSM,
			marginMD,
			paddingXL,
			padding,
			fontWeightStrong,
			paddingSM,
			colorTextDescription,
			paddingLG
		}
	} = theme.useToken();

	// ✅ Redux States
	const person = useSelector((state) => selectPeopleByPersonID(state, personID));
	const { loadingAddProfile, completeAddProfile } = useSelector((state) => state.People);

	// ✅ Local State
	const [internalVisible, setInternalVisible] = useState(false);
	const [editableUser, setEditableUser] = useState({});

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	// ✅ Update editableUser when person or personID changes
	useEffect(() => {
		if (person) {
			setEditableUser({
				personID: person?.personID,
				displayName: person?.displayName || '',
				email: person?.email || ''
			});
		}
	}, [person]);

	// ✅ Close modal function (memoized)
	const handleCancel = useCallback(() => {
		setEditableUser({
			personID: person?.personID,
			displayName: person?.displayName || '',
			email: person?.email || ''
		});
		setVisible(false);
	}, [person, setVisible]);

	// ✅ Close modal when set access only to zezamii user is successful
	useEffect(() => {
		if (completeAddProfile) handleCancel();
	}, [completeAddProfile, handleCancel]);

	// ✅ Handle email input change
	const handleEmailChange = (email) => {
		setEditableUser((prev) => ({
			...prev,
			email
		}));
	};

	// ✅ Handle "Set Associate user as Zezamii User"
	const handleSendInvite = (values) => {
		const { email } = values;

		if (personID && email) {
			dispatch(
				peopleActions.setAccessOnlyToZezamiiUser({
					personID: personID,
					email: email
				})
			);
		}
	};

	return (
		<>
			<Tooltip title={!canChangeUserType ? permissionDeniedMessage : "Change this Person's Type"} placement='top'>
				<span onClick={(e) => canChangeUserType && setVisible(true)}>
					{children ?? (
						<Button
							type='text'
							size='small'
							icon={<UserAddOutlined color='currentColor' />}
							disabled={!canChangeUserType}
						/>
					)}
				</span>
			</Tooltip>

			{/* 🔥 Modal */}
			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex align='center' justify='flex-end' style={{ marginTop: marginMD }} gap={paddingSM}>
						<Button onClick={handleCancel} disabled={loadingAddProfile} type='default'>
							Cancel
						</Button>
						<Button
							form={form}
							key='submit'
							htmlType='submit'
							type='primary'
							disabled={!editableUser.email || loadingAddProfile}
							loading={loadingAddProfile}
						>
							Send Change Invite
						</Button>
					</Flex>
				]}
				width={470}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				{/* ✅ Modal Content */}
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Change User Category
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Enter the person's email to convert them from an Access-Only User into a Zezamii User within
							the Zezamii Platform.
						</Text>
					</Col>

					<Col span={24}>
						<Form id={form} layout='vertical' onFinish={handleSendInvite} form={form}>
							{/* 🔥 Dynamic Fields for the single User */}
							<Flex gap={padding} align='middle'>
								<Form.Item
									label='Selected Person'
									required={false}
									style={{ width: '100%', fontWeight: fontWeightStrong }}
								>
									<AutoComplete
										placeholder='Select a person'
										style={{ width: '100%' }}
										value={editableUser.displayName || ''}
										disabled
									/>
								</Form.Item>

								<Form.Item
									label='User Email'
									name='email'
									required={false}
									style={{ width: '100%', fontWeight: fontWeightStrong }}
									rules={fieldsValidator.Email}
								>
									<Input
										placeholder='Assign Email'
										value={editableUser.email}
										onChange={(e) => handleEmailChange(e.target.value)}
										disabled={!editableUser.displayName}
										onPressEnter={() => form.submit()}
									/>
								</Form.Item>
							</Flex>
						</Form>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ChangePersonType;
